// ModifierBuilder.tsx - Inline version for page display
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  useMemo
} from "react";
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdRadioButtonChecked,
  MdRadioButtonUnchecked,
  MdShoppingCart,
  MdCheckCircle,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";
import { MenuItem } from "../redux/slices/menuSlice";
import { filterEnabledModifiers } from "../utils/modifierUtils";
import {
  isBuilderModifierRequired,
  isBuilderModifierMultiSelect,
  isBuilderSectionComplete,
  isBuilderSectionEnabled,
  canBuilderAddToCart,
  handleBuilderOptionSelection,
  isBuilderOptionSelected,
  getBuilderMaxSelections,
  type BuilderModifier,
  type BuilderSelection,
} from "../utils/builderModifierUtils";
import { calculateTotalPrice, formatPrice, parsePrice } from "../utils/priceUtils";

/* ------------------------------- Types ----------------------------------- */

interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface SelectedModifier {
  name: string;
  options: ModifierOption[];
}

interface ModifierBuilderProps {
  item: MenuItem;
  onAddToCart: (
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
  autoExpand?: boolean;
}

/* ------------------------ ModifierSection (memo) ------------------------- */

interface Modifier {
  name: string;
  options: ModifierOption[];
  max_options_to_select?: number;
  required?: boolean;
  type?: string;
}

type ModifierSectionProps = {
  modifier: Modifier;
  selectedModifiers: SelectedModifier[];
  onSelect: (modifierName: string, option: ModifierOption) => void;
  isSelected: (modifierName: string, optionName: string) => boolean;
  isEnabled: boolean;
  isComplete: boolean;
};

const ModifierSection: React.FC<ModifierSectionProps> = memo(
  ({ modifier, selectedModifiers, onSelect, isSelected, isEnabled, isComplete }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const selCount = selectedModifiers.find(m => m.name === modifier.name)?.options.length ?? 0;
    const maxSelectionsValue = getBuilderMaxSelections(modifier as any);
    const maxSelections = maxSelectionsValue === null ? "∞" : maxSelectionsValue;

    const toggleExpanded = () => {
      setIsExpanded(!isExpanded);
    };

    return (
      <section
        className="rounded-lg border-2 mb-4 bg-white border-gray-300"
      >
        {/* Header - Always Visible */}
        <button
          onClick={toggleExpanded}
          className="w-full p-4 flex justify-between items-center hover:bg-gray-50 cursor-pointer"
          type="button"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="text-gray-600">
              {isExpanded ? (
                <MdExpandLess className="w-6 h-6" />
              ) : (
                <MdExpandMore className="w-6 h-6" />
              )}
            </div>
            
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span>{modifier.name}</span>
                {isBuilderModifierRequired(modifier as any) && (
                  <span className="text-red-500 text-xl font-bold">*</span>
                )}
                {isComplete && <MdCheckCircle className="text-green-500" />}
              </h3>
              <p className="text-sm text-gray-600">
                {isBuilderModifierRequired(modifier as any) 
                  ? `(Choose ${maxSelections})`
                  : "(Optional)"
                }
              </p>
            </div>
          </div>
          
          <div className="text-sm font-medium text-gray-600 bg-gray-200 px-4 py-2 rounded-full">
            {selCount}/{maxSelections}
          </div>
        </button>

        {/* Options - Only When Expanded */}
        {isExpanded && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {modifier.options.map((option: ModifierOption) => {
                const selected = isSelected(modifier.name, option.name);
                const optionPrice = parsePrice(option.price);
                const isPremium = optionPrice > 0;
                const isMultiSelect = isBuilderModifierMultiSelect(modifier as any);
                
                const maxNum = typeof maxSelections === 'number' ? maxSelections : Infinity;
                const isLimitReached = isMultiSelect && selCount >= maxNum && !selected;

                const IconComponent = isMultiSelect
                  ? selected ? MdCheckBox : MdCheckBoxOutlineBlank
                  : selected ? MdRadioButtonChecked : MdRadioButtonUnchecked;

                return (
                  <button
                    key={option.id}
                    onClick={() => onSelect(modifier.name, option)}
                    disabled={isLimitReached}
                    className={`relative flex items-center space-x-2 p-3 rounded-lg border transition-all text-left ${
                      selected
                        ? "border-brand-red bg-[#FEF3F3] text-brand-red"
                        : isLimitReached
                        ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed opacity-50"
                        : "border-gray-300 hover:bg-gray-100"
                    }`}
                    type="button"
                  >
                    <IconComponent 
                      className="text-lg w-5 h-5 flex-shrink-0" 
                      style={{ color: selected ? '#EF4346' : '#D1D5DB' }}
                    />
                    <span className="font-medium text-sm">{option.name}</span>
                    {isPremium && (
                      <span className="absolute top-0 right-0 -mt-2 -mr-2 bg-gray-300 text-brand-red text-xs font-bold px-2 py-1 rounded-full">
                        {formatPrice(optionPrice)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>
    );
  }
);

ModifierSection.displayName = "ModifierSection";

/* ----------------------- ModifierBuilder ---------------------------- */

export const ModifierBuilder: React.FC<ModifierBuilderProps> = ({
  item,
  onAddToCart,
  autoExpand = false
}) => {
  const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isBuilderExpanded, setIsBuilderExpanded] = useState(autoExpand);

  // Get enabled modifiers from item
  const modifiersList = useMemo(
    () => filterEnabledModifiers(item.modifiers_list || []),
    [item.modifiers_list]
  );

  // Calculate total price using the same utility as AddToCartModal
  const totalPrice = useMemo(
    () => calculateTotalPrice(item.price, selectedModifiers, quantity),
    [item.price, selectedModifiers, quantity]
  );

  const toggleBuilder = useCallback(() => {
    setIsBuilderExpanded(prev => !prev);
  }, []);

  const handleModifierOptionSelect = useCallback(
    (modifierName: string, option: ModifierOption) => {
      const modifier = modifiersList.find(m => m.name === modifierName);
      if (!modifier) return;
      
      setSelectedModifiers((prev) =>
        handleBuilderOptionSelection(modifierName, option, prev as BuilderSelection[], modifier as BuilderModifier)
      );
    },
    [modifiersList]
  );

  const isModifierOptionSelectedLocal = useCallback(
    (modifierName: string, optionName: string): boolean =>
      isBuilderOptionSelected(modifierName, optionName, selectedModifiers as BuilderSelection[]),
    [selectedModifiers]
  );

  const isSectionComplete = useCallback((modifier: any): boolean => {
    return isBuilderSectionComplete(modifier as BuilderModifier, selectedModifiers as BuilderSelection[]);
  }, [selectedModifiers]);

  const isSectionEnabled = useCallback((index: number): boolean => {
    return isBuilderSectionEnabled(index, modifiersList as BuilderModifier[], selectedModifiers as BuilderSelection[]);
  }, [modifiersList, selectedModifiers]);

  const isFormValid = useCallback(() => {
    return canBuilderAddToCart(modifiersList as BuilderModifier[], selectedModifiers as BuilderSelection[]);
  }, [modifiersList, selectedModifiers]);

  const handleAddToCartClick = useCallback(async () => {
    if (!isFormValid()) return;

    setIsAddingToCart(true);
    setError(null);

    try {
      await onAddToCart(item, quantity, null, selectedModifiers);
      // Reset quantity and selections after successful cart addition
      setQuantity(1);
      setSelectedModifiers([]);
    } catch (err) {
      console.error("Add to cart failed:", err);
      setError("Failed to add item to cart. Please try again.");
    } finally {
      setIsAddingToCart(false);
    }
  }, [item, quantity, selectedModifiers, onAddToCart, isFormValid]);

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
      {/* Collapsible Item Header */}
      <button
        onClick={toggleBuilder}
        className="w-full p-6 flex justify-between items-start hover:bg-gray-50 transition-colors cursor-pointer"
        type="button"
      >
        <div className="flex items-center gap-4 flex-1">
          <div className="text-gray-600">
            {isBuilderExpanded ? (
              <MdExpandLess className="w-6 h-6" />
            ) : (
              <MdExpandMore className="w-6 h-6" />
            )}
          </div>
          <div className="flex-1 text-left">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{item.name}</h2>
            {item.description && (
              <p className="text-gray-600 text-sm">{item.description}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-gray-900">
              {formatPrice(parsePrice(item.price))}
            </p>
          </div>
          {item.image && (
            <img 
              src={item.image} 
              alt={item.name}
              className="w-24 h-24 object-cover rounded-lg"
            />
          )}
        </div>
      </button>

      {/* Builder Content - Only When Expanded */}
      {isBuilderExpanded && (
        <div className="px-6 pb-6">
          <div className="border-t border-gray-200 pt-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Modifier Sections */}
            <div className="mb-6">
              {modifiersList.map((modifier, idx) => (
                <ModifierSection
                  key={modifier.name}
                  modifier={modifier}
                  selectedModifiers={selectedModifiers}
                  onSelect={handleModifierOptionSelect}
                  isSelected={isModifierOptionSelectedLocal}
                  isEnabled={isSectionEnabled(idx)}
                  isComplete={isSectionComplete(modifier)}
                />
              ))}
            </div>

            {/* Footer with Quantity and Add to Cart */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {selectedModifiers.length > 0 && (
                  <p className="font-medium">
                    Includes: {selectedModifiers.flatMap(m => m.options.map(o => o.name)).join(', ')}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                {/* Quantity Controls */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded bg-white hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Decrease quantity"
                    type="button"
                  >
                    <span className="text-xl font-bold text-gray-700">−</span>
                  </button>
                  <span className="w-8 text-center font-bold text-gray-900">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 h-8 flex items-center justify-center rounded bg-white hover:bg-gray-200 transition-colors"
                    aria-label="Increase quantity"
                    type="button"
                  >
                    <span className="text-xl font-bold text-gray-700">+</span>
                  </button>
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={handleAddToCartClick}
                  disabled={isAddingToCart || !isFormValid()}
                  style={{
                    backgroundColor: isFormValid() && !isAddingToCart ? '#EF4345' : 'rgb(209, 213, 219)',
                    color: isFormValid() && !isAddingToCart ? 'white' : 'rgb(75, 85, 99)'
                  }}
                  className={`font-bold py-3 px-8 rounded-lg transition-colors text-base whitespace-nowrap flex items-center gap-2 ${
                    isFormValid() && !isAddingToCart
                      ? "hover:bg-red-600"
                      : "cursor-not-allowed"
                  }`}
                  type="button"
                >
                  {isAddingToCart ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <MdShoppingCart className="text-lg" />
                      <span>Add to Cart - {formatPrice(totalPrice)}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModifierBuilder;
